<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Company Menu" @click="$router.push('/company')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>         
                    <help-link appId="company" pageId="linked-account-manage" color="white"></help-link>          
                    <v-btn v-if="hasAddRights" fab small color="primary" title="Add New Company" @click="dialogAddLinkAccount=true">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-card class="elevation-1 mt-5">
                <v-toolbar flat height="45px">
                    <div :class="isMobile ? 'wp-50 mb-10' : 'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search company name" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : headers"
                    :items="linked_accounts_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1" @click="viewDetails(props.item.linkCompanyId)">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container">
                                            <v-avatar size="30">
                                                <v-img :src="commonfn.getCompanyLogoUrlByAccount(props.item.accountNumber, props.item.logoFileName)"></v-img>
                                            </v-avatar>
                                            {{ props.item.companyName }}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="card-layout-mobile">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Employees</div>
                                                    <div>{{ props.item.employeeCount }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs12>
                                                    <div class="caption grey--text text--darken-1">Admin Users</div>
                                                    <div class="ml-0">
                                                        <v-chip small v-for="(item,index) in props.item.admins" :key="index">
                                                            <v-avatar size="30">
                                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(item.parentUserId)"></v-img>
                                                            </v-avatar>
                                                            {{item.firstName}} {{item.lastName}}
                                                        </v-chip> 
                                                    </div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="viewDetails(props.item.linkCompanyId)" title="Click to manage linked company account" class="clickable">
                            <td>
                                <v-img :src="commonfn.getCompanyLogoUrlByAccount(props.item.accountNumber, props.item.logoFileName)" width="40"></v-img>
                            </td>
                            <td>{{ props.item.companyName }}</td>
                            <td>{{ props.item.employeeCount }}</td>
                            <td>
                                <div class="ml-0">
                                    <v-chip small v-for="(item,index) in props.item.admins" :key="index">
                                        <v-avatar size="30">
                                            <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(item.parentUserId)"></v-img>
                                        </v-avatar>
                                        {{item.firstName}} {{item.lastName}}
                                    </v-chip> 
                                </div>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card>
        </v-flex>
        <v-dialog v-model="dialogAddLinkAccount" persistent width="600">
            <add-link-account :dialogAddLinkAccount.sync="dialogAddLinkAccount" @send="viewDetails"></add-link-account>
        </v-dialog>
    </v-layout>
</template>

<script>

import commonfn from '@/common/functions'
import companyApi from '@/api/company'
import awsS3Helper from '@/helpers/aws-s3'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getSort, getRowsPerPage, getSearchKeyword, updateRowsPerPage, updateSort, updateSearchKeyword } from '@/helpers/user-prefs'
import { hasAddRights } from '@/helpers/access-permissions'

export default {
    name: 'LinkedAccounts',
    metaInfo: {
        title: 'Company Linked Accounts',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Company Linked Accounts' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-link-account': ()=> import("@/components/Company/AddLinkedAccount"),
    },
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)

        return {
            rowsPerPageItems,
            commonfn,
            awsS3Helper,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: getSearchKeyword(routeName),
            error: false,
            errorMessage: '',
            loading: false,
            headers: [
                {text:'Company Logo', align:'left', value:'projectCode', width:'10%', sortable:false},
                {text:'Company Name', align:'left', value:'companyName', width:'40%', sortable:true},
                {text:'Employees', align:'left', value:'employeeCount', width:'10%', sortable:true},
                {text:'Admin Users', align:'left', value:'', width: '40%', sortable:false},
            ],
            dialogAddLinkAccount: false,
            linked_accounts: []
        }
    },
    methods: {
        viewDetails(linkCompanyId){
            this.$router.push(`/company/link/${linkCompanyId}`)
        },
        async getLinkedAccounts(show_loading) {
            try {
                this.loading = show_loading
                const response = await companyApi.getLinkedAccounts()
                if (response && response.status == 200) {
                    this.linked_accounts = response.data.accounts
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    async created() {
        this.getLinkedAccounts(true)

        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Linked Company Accounts'
        })
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },        
        linked_accounts_filtered() {
            if (this.linked_accounts.length) {
                if (this.search) {
                    const temp = this.linked_accounts.filter(item => {
                        return item.companyName.toLowerCase().includes(this.search.toLowerCase())
                    })
                    return temp
                }
                return this.linked_accounts
            }
        },   
    }
}
</script>
<style scoped>
.card-layout-mobile {
    padding:0px 23px 0px 23px;
}
</style>